import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Search, Loader2, Trash2, ExternalLink, Building2,
  CheckCircle2, XCircle, Clock, Pencil, Eye,
} from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const KINDS = [
  { key: "all", label: "Alles" },
  { key: "huur", label: "Huur" },
  { key: "koop", label: "Koop" },
];

const SELLER_TYPES = [
  { key: "all", label: "Iedereen" },
  { key: "makelaar", label: "Makelaar" },
  { key: "particulier", label: "Particulier" },
];

const MODERATION = [
  { key: "pending", label: "Nog goedkeuren" },
  { key: "approved", label: "Goedgekeurd" },
  { key: "rejected", label: "Afgewezen" },
  { key: "all", label: "Alles" },
];

const MOD_BADGE = {
  pending: { label: "Wacht op review", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Live", cls: "bg-success/15 text-success" },
  rejected: { label: "Afgewezen", cls: "bg-danger/10 text-danger" },
};

const fmtPrice = (n) => (typeof n === "number" ? `€${n.toLocaleString("nl-NL")}` : "—");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};
const publicUrl = (l) =>
  l.kind === "koop" ? `/te-koop/${l.slug || l.id}` : `/woning/${l.slug || l.id}`;

const AdminBrokerListings = () => {
  useSeo({ title: "Makelaar listings · Admin" });
  const [kind, setKind] = useState("all");
  const [sellerType, setSellerType] = useState("all");
  const [moderation, setModeration] = useState("pending");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => {
    setData(null);
    setError("");
    const params = new URLSearchParams({ kind, seller_type: sellerType, moderation, limit: "200" });
    if (search.trim()) params.set("q", search.trim());
    api(`/admin/broker-listings?${params.toString()}`)
      .then(setData)
      .catch((e) => setError(e.message));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, sellerType, moderation, search]);

  const listings = data?.listings || [];

  const stats = useMemo(() => {
    const base = { total: listings.length, huur: 0, koop: 0 };
    for (const l of listings) base[l.kind] = (base[l.kind] || 0) + 1;
    return base;
  }, [listings]);

  const onDelete = async (l) => {
    const label = `${l.title || l.street || l.id}${l.city ? ` · ${l.city}` : ""}`;
    if (!window.confirm(`Weet je zeker dat je deze listing wilt verwijderen?\n\n${label}`)) return;
    setBusy(l.id);
    try {
      await api(`/admin/broker-listings/${l.kind}/${l.id}`, { method: "DELETE" });
      setData((prev) =>
        prev ? { ...prev, listings: prev.listings.filter((x) => x.id !== l.id) } : prev,
      );
    } catch (e) {
      alert(`Verwijderen mislukt: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const moderate = async (l, action) => {
    const note = action === "reject" ? (window.prompt("Reden van afwijzing (optioneel):", "") ?? "") : "";
    setBusy(l.id);
    try {
      await api(`/admin/broker-listings/${l.kind}/${l.id}/moderate`, {
        method: "POST",
        body: JSON.stringify({ action, note }),
      });
      load();
    } catch (e) {
      alert(`Actie mislukt: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="back-admin">
        <ArrowLeft size={14} /> Admin
      </Link>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-navy" data-testid="broker-listings-title">
            Makelaar listings
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Alle woningen die makelaars via het platform hebben geplaatst — controleer en verwijder waar nodig.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-xl" data-testid="refresh-btn">
          <RefreshCw size={16} className="mr-2" /> Ververs
        </Button>
      </div>

      {/* Stats */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Totaal</div>
          <div className="mt-1 font-display text-2xl font-extrabold text-navy" data-testid="stat-total">{stats.total}</div>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Huur</div>
          <div className="mt-1 font-display text-2xl font-extrabold text-brandblue" data-testid="stat-huur">{stats.huur}</div>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Koop</div>
          <div className="mt-1 font-display text-2xl font-extrabold text-success" data-testid="stat-koop">{stats.koop}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => setKind(k.key)}
              data-testid={`filter-kind-${k.key}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                kind === k.key ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {SELLER_TYPES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSellerType(s.key)}
              data-testid={`filter-seller-${s.key}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                sellerType === s.key ? "bg-brandblue text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {MODERATION.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setModeration(m.key)}
              data-testid={`filter-mod-${m.key}`}
              className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                moderation === m.key ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {m.key === "pending" && <Clock size={13} />}
              {m.label}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(q);
          }}
          className="relative ml-auto w-full sm:w-80"
        >
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Zoek op titel, straat, stad of slug…"
            className="rounded-xl pl-9"
            data-testid="search-input"
          />
        </form>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
        {error ? (
          <div className="p-8 text-center text-sm text-danger" data-testid="error">{error}</div>
        ) : !data ? (
          <div className="flex items-center justify-center p-16">
            <Loader2 className="animate-spin text-brandblue" size={28} />
          </div>
        ) : listings.length === 0 ? (
          <div className="p-16 text-center text-sm text-slate-500" data-testid="empty-state">
            <Building2 size={28} className="mx-auto mb-3 text-slate-300" />
            Geen makelaar-listings gevonden{search ? ` voor "${search}"` : ""}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">Woning</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Prijs</th>
                  <th className="px-5 py-3">Views</th>
                  <th className="px-5 py-3">Makelaar</th>
                  <th className="px-5 py-3">Aangemaakt</th>
                  <th className="px-5 py-3 text-right">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {listings.map((l) => (
                  <tr key={`${l.kind}-${l.id}`} data-testid={`row-${l.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-semibold text-navy">
                        {l.title || `${l.street || "—"}${l.house_number ? " " + l.house_number : ""}`}
                      </div>
                      <div className="text-xs text-slate-400">
                        {l.city || "—"}{l.postcode ? ` · ${l.postcode}` : ""}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                            l.kind === "koop" ? "bg-success/15 text-success" : "bg-brandblue-light text-brandblue"
                          }`}
                        >
                          {l.kind === "koop" ? "Koop" : "Huur"}
                        </span>
                        {l.seller_type === "particulier" && (
                          <span className="inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900">
                            Particulier
                          </span>
                        )}
                        {(() => {
                          const m = MOD_BADGE[l.moderation_status || "approved"];
                          return (
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${m.cls}`}>
                              {m.label}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-5 py-3 font-semibold text-navy">{fmtPrice(l.price)}</td>
                    <td className="px-5 py-3" data-testid={`views-${l.id}`}>
                      {l.kind === "koop" && l.views ? (
                        <div>
                          <div className="font-semibold text-navy">{l.views.total}</div>
                          <div className="text-xs text-slate-400">{l.views.last_7d} laatste 7d</div>
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {l.broker?.id ? (
                        <Link to={`/admin/users/${l.broker.id}`} className="text-brandblue hover:underline" data-testid={`broker-${l.id}`}>
                          {l.broker.agency_name || l.broker.name || l.broker.email || l.broker.id}
                        </Link>
                      ) : (
                        <span className="text-slate-400">Onbekend</span>
                      )}
                      {l.broker?.email && (
                        <div className="text-xs text-slate-400">{l.broker.email}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-500">{fmtDate(l.created_at)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/broker-listings/${l.kind}/${l.id}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-brandblue-light px-2.5 py-1.5 text-xs font-semibold text-brandblue hover:bg-brandblue-light/70"
                          data-testid={`edit-${l.id}`}
                        >
                          <Pencil size={13} /> Bewerken
                        </Link>
                        <Link
                          to={`/admin/preview/${l.kind}/${l.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                          data-testid={`preview-${l.id}`}
                        >
                          <Eye size={13} /> Voorbeeld
                        </Link>
                        {(l.moderation_status || "approved") !== "approved" && (
                          <button
                            type="button"
                            onClick={() => moderate(l, "approve")}
                            disabled={busy === l.id}
                            className="inline-flex items-center gap-1 rounded-lg bg-success/15 px-2.5 py-1.5 text-xs font-semibold text-success hover:bg-success/25 disabled:opacity-50"
                            data-testid={`approve-${l.id}`}
                          >
                            {busy === l.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                            Goedkeuren
                          </button>
                        )}
                        {(l.moderation_status || "approved") !== "rejected" && (
                          <button
                            type="button"
                            onClick={() => moderate(l, "reject")}
                            disabled={busy === l.id}
                            className="inline-flex items-center gap-1 rounded-lg bg-amber-100 px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-200 disabled:opacity-50"
                            data-testid={`reject-${l.id}`}
                          >
                            <XCircle size={13} /> Afwijzen
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onDelete(l)}
                          disabled={busy === l.id}
                          className="inline-flex items-center gap-1 rounded-lg bg-danger/10 px-2.5 py-1.5 text-xs font-semibold text-danger hover:bg-danger/20 disabled:opacity-50"
                          data-testid={`delete-${l.id}`}
                        >
                          {busy === l.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                          Verwijder
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBrokerListings;
